import { useEffect, useState } from "react";
import Papa from "papaparse";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { useTranslation } from "react-i18next";
import { FileUploadButton } from "../../common-ui/FileUploadButton";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BulkInvitation } from "./userInvitationUtils";
import { useAppSelector } from "../../app/hooks";
import { v4 as uuidv4 } from "uuid";
import { selectUserCognitoGroups } from "../Users/usersSlice";
import { CognitoUserGroups } from "../Users/userUtils";
import { s3Files } from "../../app/constants";
import { ErrorPage } from "../../common-ui/ErrorPage";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../Firebase/firebaseSlice";

const pageDetails: PageWrapperProps = {
  className: "UserInvitationsBulkUpload",
  headerText: "User Invitations - Bulk Upload"
};

export const UserInvitationsBulkUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<BulkInvitation[] | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUserCognitoGroups = useAppSelector(selectUserCognitoGroups);
  const flags = useAppSelector(selectFeatureFlags);
  const flagsStatus = useAppSelector(selectFeatureFlagsStatus);
  const flagsError = useAppSelector(selectFeatureFlagsError);
  const { allow_bulk_invite } = flags;

  // default to true to avoid flashing the error screen when the flag is true
  const allowBulkInvite =
    flagsStatus === "succeeded" ? allow_bulk_invite : true;

  const isSysAdmin = currentUserCognitoGroups?.includes(
    CognitoUserGroups.SysAdmins
  );

  const templateFile = isSysAdmin
    ? s3Files.BulkInviteSysAdmin
    : s3Files.BulkInviteProgramAdmin;

  useEffect(() => {
    if (file) {
      Papa.parse<BulkInvitation>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setParsedData(
            results.data.map((d) => ({
              ...d,
              programIdFromFile: d.programId,
              requestId: uuidv4()
            }))
          );
        }
      });
    }
  }, [file]);

  useEffect(() => {
    if (parsedData) navigate("/invitations/bulkinvite", { state: parsedData });
  }, [parsedData]);

  if (!allowBulkInvite) {
    return <ErrorPage />;
  }

  return (
    <PageWrapper {...pageDetails}>
      <div>
        {t(
          "Upload a comma-delimited .csv file that includes the fields below for each user. A template file is provided for assistance."
        )}
        <ul>
          <li>{t("First Name")}</li>
          <li>{t("Last Name")}</li>
          <li>{t("Email Address")}</li>
          <li>{t("NPI (if applicable)")}</li>
          <li>{t("PGY (if applicable)")}</li>
          <li>{t("Job Title (if applicable)")}</li>
          <li>
            {t(
              'Role (can be "Trainee", "Attending", or "ProgramAdmin" without quotes)'
            )}
          </li>
          <li>
            {t(
              'Trainee Type (if applicable, can be "Categorical", "DesignatedPreliminary", "NonDesignatedPreliminary", or "Fellow" without quotes)'
            )}
          </li>
          {isSysAdmin && <li>{t("Program ID")}</li>}
        </ul>
        {t('Click "Upload" and select the completed file.')}
      </div>
      <div>
        <Button href={templateFile} download variant="outlined">
          {t("Download Template")}
        </Button>
        <FileUploadButton setFile={setFile} />
      </div>
    </PageWrapper>
  );
};
